import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { makeStyles } from 'tss-react/mui';
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';
import type { Action } from '@shared/helpers/pipeline/update-engine.models';
import type {
  Baseline,
  ProjectMarketEstimates,
} from '@shared/models/project.model';
import { useIsReadonly } from '@client/hooks/use-user.hooks';
import { Icon } from '@ui/Icon';
import ElementListRow, {
  ElementListRowHandle,
} from '@features/proposals/ElementListRow/ElementListRow';
import {
  getActionNumber,
  groupActionChanges,
  buildActionName,
} from '@shared/helpers/actions/action.helpers';
import {
  getElementById,
  flattenElements,
} from '@shared/helpers/recursive-element.helpers';
import { ElementKind } from '@shared/models/project.model';
import { useElementListGroupBy } from '@features/elements/helpers/element-grouping.helpers';
import { getSbefGroupCodeFromElementOrRecipe } from '@shared/helpers/sbef.helpers';
import { UNICODE } from '@shared/models/unicode.model';
import { ActionContextMenu } from './ActionContextMenu';
import { ActionChangeRow } from './ActionChangeRow';
import { ActionElementRow } from './ActionElementRow';
import { KPI_CARD_VALUE_LETTER_SPACING } from './ProposalKpiSection';

const ACTION_ICON_SIZE = 12;
const ActionIcon = <Icon category="ui" icon="action" size={ACTION_ICON_SIZE} />;

const ELEMENT_SHOW_INITIAL = 3;
const ELEMENT_COLLAPSE_THRESHOLD = 4;

const ShowMoreRow: React.FC<{ count: number; onClick: () => void }> = ({
  count,
  onClick,
}) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Box
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: 'flex',
        width: '100%',
        cursor: 'pointer',
      }}
    >
      <ElementListRow
        name={`Show ${count} more`}
        nameReadonly
        hideQuantity
        resizable={false}
        hideSurfaceBackground
        leftGap={8}
        depth={1}
        height={20}
        iconSlot={null}
        prefixIconSlots={[null]}
        nameDisplayStyle={{
          color: hovered ? 'rgba(0,0,0,1)' : 'rgba(0,0,0,0.4)',
          WebkitTextFillColor: hovered ? 'rgba(0,0,0,1)' : 'rgba(0,0,0,0.4)',
          transition: 'color 0.15s ease',
        }}
      />
    </Box>
  );
};

/**
 * Fixed column widths so action-row KPI values stay a stable distance from the
 * right (independent of compact number string length).
 */
const ACTION_KPI_CO2E_SLOT_PX = 48;
const ACTION_KPI_COST_SLOT_PX = 36;
const ACTION_KPI_LEADING_GAP_PX = 4;
const ACTION_KPI_LEADING_MAX_WIDTH_PX =
  ACTION_KPI_CO2E_SLOT_PX + ACTION_KPI_LEADING_GAP_PX + ACTION_KPI_COST_SLOT_PX;
/**
 * Name field `paddingRight` for the action KPI + kebab (absolute) cluster.
 * Calibrated to clear the icons; 24px leaner than the first pass to shrink the
 * empty strip before the first KPI.
 */
const ACTION_NAME_RIGHT_RESERVE_PX = 108;

/** Matches `ProposalKpiSection` carbon KPI arrow chip colors. */
const CARBON_ARROW_POSITIVE_BG = '#c8ffd6';
const CARBON_ARROW_POSITIVE_COLOR = '#266b37';
const CARBON_ARROW_NEGATIVE_BG = '#fbe7ea';
const CARBON_ARROW_NEGATIVE_COLOR = '#803943';

type ActionListItemProps = {
  action: Action;
  version: Baseline;
  onRename: (actionId: string, name: string) => void;
  onRemove: (actionId: string) => void;
  onElementClick?: (elementId: string) => void;
  /**
   * This action's share of the proposal CO2e delta (KPI1), same units as the
   * carbon KPI card. Omitted when unavailable.
   */
  actionCo2eDelta?: number;
  /**
   * This action's share of the proposal cost delta (KPI2), in SEK. Omitted
   * when cost KPIs are disabled or the value is unavailable.
   */
  actionCostDeltaSek?: number;
  /**
   * Market estimates snapshot for product carbon percentile display on change rows.
   */
  marketEstimates?: ProjectMarketEstimates;
  /**
   * Shows skeletons in the KPI slots while the sign-corrected per-action series
   * computes off the render path (DEV-3703). `'co2e'` reserves only the carbon
   * slot; `'co2e+cost'` also reserves the cost slot.
   */
  kpiLoadingSlots?: 'co2e' | 'co2e+cost';
};

const useActionKpiMetricStyles = makeStyles()(() => ({
  metric: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    flexShrink: 0,
    gap: 5,
    justifyContent: 'flex-start',
    minWidth: 0,
    overflow: 'hidden',
    width: ACTION_KPI_COST_SLOT_PX,
  },
  arrow: {
    fontSize: 10,
    height: 10,
    width: 10,
    opacity: 0.7,
  },
  value: {
    color: 'rgba(0,0,0,0.55)',
    flex: 1,
    fontSize: 11,
    fontWeight: 400,
    lineHeight: 1.15,
    minWidth: 0,
    overflow: 'hidden',
    textAlign: 'left',
    textOverflow: 'clip',
    whiteSpace: 'nowrap',
  },
  costArrowSpacer: {
    flexShrink: 0,
    height: 10,
    width: 10,
  },
}));

const useActionKpiLeadingRowStyles = makeStyles()(() => ({
  row: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    flexShrink: 0,
    gap: 4,
  },
}));

const useActionCo2eKpiStyles = makeStyles()(() => ({
  co2eMetric: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    flexShrink: 0,
    gap: 5,
    justifyContent: 'flex-start',
    minWidth: 0,
    overflow: 'hidden',
    width: ACTION_KPI_CO2E_SLOT_PX,
  },
  co2eArrowSpacer: {
    flexShrink: 0,
    height: 12,
    width: 12,
  },
  co2eArrowChip: {
    alignItems: 'center',
    border: '1px solid rgba(0,0,0,0.1)',
    borderRadius: 2,
    display: 'flex',
    flexShrink: 0,
    height: 12,
    justifyContent: 'center',
    opacity: 0.8,
    width: 12,
  },
  co2eValue: {
    color: 'rgba(0,0,0,0.55)',
    flex: 1,
    fontSize: 11,
    fontWeight: 600,
    lineHeight: 1.15,
    minWidth: 0,
    overflow: 'hidden',
    textAlign: 'left',
    textOverflow: 'clip',
    whiteSpace: 'nowrap',
  },
}));

/** KPI1 (carbon): same chip + green/red as the first KPI card in the proposal panel. */
const ActionCo2eKpiSlot: React.FC<{ value: number }> = ({ value }) => {
  const { classes } = useActionCo2eKpiStyles();
  const isZero = value === 0;
  const showArrow = !isZero;
  const isEmissionsUp = value > 0;
  const chipBg = isEmissionsUp
    ? CARBON_ARROW_NEGATIVE_BG
    : CARBON_ARROW_POSITIVE_BG;
  const chipColor = isEmissionsUp
    ? CARBON_ARROW_NEGATIVE_COLOR
    : CARBON_ARROW_POSITIVE_COLOR;
  const ArrowIcon = value < 0 ? ArrowDownwardIcon : ArrowUpwardIcon;
  return (
    <Box className={classes.co2eMetric}>
      {showArrow ? (
        <Box
          className={classes.co2eArrowChip}
          sx={{ backgroundColor: chipBg, color: chipColor }}
        >
          <ArrowIcon sx={{ fontSize: 10 }} />
        </Box>
      ) : (
        <Box className={classes.co2eArrowSpacer} aria-hidden />
      )}
      <Typography
        component="span"
        className={classes.co2eValue}
        sx={{
          letterSpacing: KPI_CARD_VALUE_LETTER_SPACING,
          ...(isZero && { opacity: 0.3 }),
        }}
      >
        {isZero
          ? UNICODE.DASH.EN_DASH
          : getDisplayNumber(Math.abs(value), { notation: 'compact' })}
      </Typography>
    </Box>
  );
};

/** KPI2 (cost): up / down matches the proposal cost KPI card. */
const ActionCostKpiSlot: React.FC<{
  value: number;
  /**
   * Hides the figure but keeps the slot width and arrow (e.g. row hover);
   * uses `visibility: hidden` so layout does not shift.
   */
  hideValue?: boolean;
}> = ({ value, hideValue = false }) => {
  const { classes } = useActionKpiMetricStyles();
  const isZero = value === 0;
  const ArrowIcon = value <= 0 ? ArrowDownwardIcon : ArrowUpwardIcon;
  return (
    <Box className={classes.metric}>
      {isZero ? (
        <Box
          className={classes.costArrowSpacer}
          sx={{ margin: '0px -4px 0px -2px' }}
          aria-hidden
        />
      ) : (
        <ArrowIcon
          className={classes.arrow}
          sx={{ margin: '0px -4px 0px -2px' }}
        />
      )}
      <Typography
        component="span"
        className={classes.value}
        sx={{
          letterSpacing: KPI_CARD_VALUE_LETTER_SPACING,
          visibility: hideValue ? 'hidden' : 'visible',
          ...(isZero && { opacity: 0.3 }),
        }}
      >
        {isZero
          ? UNICODE.DASH.EN_DASH
          : getDisplayNumber(Math.abs(value), { notation: 'compact' })}
      </Typography>
    </Box>
  );
};

const ActionKpiLeadingSlots: React.FC<{
  co2e?: number;
  costSek?: number;
  /** When true, cost digits are hidden but the cost column width and arrow stay. */
  hideCostValue?: boolean;
  /** Renders skeletons in the given slots instead of values (series computing). */
  loadingSlots?: 'co2e' | 'co2e+cost';
}> = ({ co2e, costSek, hideCostValue = false, loadingSlots }) => {
  const { classes } = useActionKpiLeadingRowStyles();
  if (loadingSlots) {
    return (
      <Box className={classes.row}>
        <Skeleton variant="text" width={ACTION_KPI_CO2E_SLOT_PX} />
        {loadingSlots === 'co2e+cost' && (
          <Skeleton
            variant="text"
            width={ACTION_KPI_COST_SLOT_PX}
            sx={{ visibility: hideCostValue ? 'hidden' : 'visible' }}
          />
        )}
      </Box>
    );
  }
  if (co2e === undefined && costSek === undefined) return null;
  return (
    <Box className={classes.row}>
      {co2e !== undefined && <ActionCo2eKpiSlot value={co2e} />}
      {costSek !== undefined && (
        <ActionCostKpiSlot value={costSek} hideValue={hideCostValue} />
      )}
    </Box>
  );
};

export const ActionListItem: React.FC<ActionListItemProps> = ({
  action,
  version,
  onRename,
  onRemove,
  onElementClick,
  actionCo2eDelta,
  actionCostDeltaSek,
  marketEstimates,
  kpiLoadingSlots,
}) => {
  const { classes } = useStyles();
  const isReadonly = useIsReadonly();
  const rowRef = useRef<ElementListRowHandle>(null);
  const lsKey = `actions-list-expanded:${action.id}`;
  const [expanded, setExpanded] = useState(
    () => localStorage.getItem(lsKey) === 'true',
  );
  const handleExpandedChange = useCallback(
    (next: boolean) => {
      setExpanded(next);
      localStorage.setItem(lsKey, String(next));
    },
    [lsKey],
  );

  const actionNumber = getActionNumber(action.id, version);
  const namePlaceholder =
    buildActionName(action.changes, version) ??
    (actionNumber > 0 ? `Action ${actionNumber}` : 'Action');

  const kebabLeadingNamePaddingRight = useMemo(() => {
    const hasCo2 =
      actionCo2eDelta !== undefined || kpiLoadingSlots !== undefined;
    const hasCost =
      actionCostDeltaSek !== undefined || kpiLoadingSlots === 'co2e+cost';
    const kpiCount = (hasCo2 ? 1 : 0) + (hasCost ? 1 : 0);
    if (kpiCount === 0) return undefined;
    const kpiBlockWidth =
      (hasCo2 ? ACTION_KPI_CO2E_SLOT_PX : 0) +
      (hasCo2 && hasCost ? ACTION_KPI_LEADING_GAP_PX : 0) +
      (hasCost ? ACTION_KPI_COST_SLOT_PX : 0);
    return `${ACTION_NAME_RIGHT_RESERVE_PX - (ACTION_KPI_LEADING_MAX_WIDTH_PX - kpiBlockWidth)}px`;
  }, [actionCo2eDelta, actionCostDeltaSek, kpiLoadingSlots]);

  const [groupBy] = useElementListGroupBy();

  const groups = useMemo(() => {
    const rawGroups = groupActionChanges(action.changes);

    const flatTree = flattenElements(version);
    const treeIndexMap = new Map<string, number>(
      flatTree.map((el, i) => [el.id, i]),
    );

    const sbefActive = groupBy === 'sbef';

    return rawGroups.map((g) => ({
      ...g,
      elementIds: [...g.elementIds].sort((aId, bId) => {
        if (sbefActive) {
          const aEl = getElementById(version, aId, false);
          const bEl = getElementById(version, bId, false);
          if (aEl && bEl) {
            const aCode = getSbefGroupCodeFromElementOrRecipe(aEl) ?? '￿';
            const bCode = getSbefGroupCodeFromElementOrRecipe(bEl) ?? '￿';
            if (aCode !== bCode) return aCode.localeCompare(bCode);
          }
        }
        return (
          (treeIndexMap.get(aId) ?? Infinity) -
          (treeIndexMap.get(bId) ?? Infinity)
        );
      }),
    }));
  }, [action.changes, version, groupBy]);

  const handleNameSave = useCallback(
    (name: string) => onRename(action.id, name),
    [action.id, onRename],
  );

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Record<number, boolean>>(
    {},
  );

  useEffect(() => {
    if (!expanded) setExpandedGroups({});
  }, [expanded]);

  const handleKebabClick = useCallback<React.MouseEventHandler<HTMLElement>>(
    (e) => {
      e.stopPropagation();
      setMenuAnchor(e.currentTarget);
    },
    [],
  );

  const handleMenuClose = useCallback(() => setMenuAnchor(null), []);

  return (
    <Box className={classes.root}>
      <ElementListRow
        ref={rowRef}
        name={action.name ?? ''}
        namePlaceholder={namePlaceholder}
        iconSlot={ActionIcon}
        hideQuantity
        resizable={false}
        hideSurfaceBackground
        leftGap={8}
        kebabMenuOpen={!!menuAnchor}
        expandable={groups.length > 0}
        expanded={expanded}
        onExpandedChange={handleExpandedChange}
        onNameSave={isReadonly ? undefined : handleNameSave}
        onKebabClick={isReadonly ? undefined : handleKebabClick}
        nameDisplayStyle={{
          fontWeight: 500,
          letterSpacing: '0.1em',
          color: 'rgba(0,0,0,0.5)',
          WebkitTextFillColor: 'rgba(0,0,0,0.5)',
        }}
        kebabLeadingNamePaddingRight={kebabLeadingNamePaddingRight}
        kpiClusterTranslateX={
          actionCo2eDelta === undefined &&
          actionCostDeltaSek === undefined &&
          !kpiLoadingSlots
            ? 0
            : 8
        }
        kebabLeadingSlot={
          actionCo2eDelta === undefined &&
          actionCostDeltaSek === undefined &&
          !kpiLoadingSlots
            ? undefined
            : (isRowHovered) => (
                <ActionKpiLeadingSlots
                  co2e={actionCo2eDelta}
                  costSek={actionCostDeltaSek}
                  hideCostValue={isRowHovered}
                  loadingSlots={kpiLoadingSlots}
                />
              )
        }
      />

      {!isReadonly && (
        <ActionContextMenu
          anchor={menuAnchor}
          onClose={handleMenuClose}
          onRename={() => rowRef.current?.startRename()}
          onDelete={() => onRemove(action.id)}
        />
      )}

      {expanded && groups.length > 0 && (
        <Box
          className={classes.section}
          sx={{
            pb: 1,
          }}
        >
          {groups.map((group, gi) => {
            const elementRows = group.elementIds
              .map((id) => getElementById(version, id, false))
              .filter(
                (el) =>
                  !!el &&
                  (el.kind === ElementKind.Element ||
                    el.kind === ElementKind.Product),
              );

            const groupExpanded = expandedGroups[gi] === true;
            const truncate =
              elementRows.length > ELEMENT_COLLAPSE_THRESHOLD && !groupExpanded;
            const visibleRows = truncate
              ? elementRows.slice(0, ELEMENT_SHOW_INITIAL)
              : elementRows;
            const hiddenCount = elementRows.length - ELEMENT_SHOW_INITIAL;

            return (
              <React.Fragment key={gi}>
                {visibleRows.map((el) => (
                  <ActionElementRow
                    key={el.id}
                    element={el}
                    version={version}
                    onElementClick={
                      onElementClick ? () => onElementClick(el.id) : undefined
                    }
                  />
                ))}
                {truncate && (
                  <ShowMoreRow
                    count={hiddenCount}
                    onClick={() =>
                      setExpandedGroups((prev) => ({ ...prev, [gi]: true }))
                    }
                  />
                )}
                {group.changes.map((change, ci) => (
                  <ActionChangeRow
                    key={ci}
                    change={change}
                    version={version}
                    marketEstimates={marketEstimates}
                  />
                ))}
              </React.Fragment>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  changeGroup: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  elementRow: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minWidth: 0,
    overflow: 'hidden',
    padding: theme.spacing(0.5, 1.5, 0.5, 4),
  },
  firstElementRow: {
    position: 'relative' as const,
    width: '100%',
  },
  changeRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    padding: theme.spacing(0.25, 1.5, 0.25, 5),
    minWidth: 0,
  },
  changeLabel: {
    flexShrink: 0,
    color: 'rgba(0,0,0,0.6)',
    fontWeight: 600,
  },
  changeValue: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: 'rgba(0,0,0,0.45)',
  },
  overflowLabel: {
    position: 'absolute' as const,
    right: theme.spacing(1),
    top: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.7,
    },
  },
  overflowText: {
    color: 'rgba(0,0,0,0.45)',
    fontWeight: 600,
  },
}));
